import {
  KnownHistoricDeliveryStatsAggregationPeriod,
  KnownHistoricDeliveryStatsEventTypes
} from '@groundfloor-platform/mgmt-service-g-sdk';
import dayjs from 'dayjs';
import { useImmer } from 'use-immer';
import { useQueryHistoricStats } from '../dashboard.queries';

export type HistoricOrderStatsEventTypeOptions = Exclude<
  KnownHistoricDeliveryStatsEventTypes,
  KnownHistoricDeliveryStatsEventTypes.Asterisk
>;

interface HistoricStatsState {
  eventType: HistoricOrderStatsEventTypeOptions;
  aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod;
  fromDate: string;
  toDate: string;
}

export function useHistoricStats(projectID: string) {
  const [state, setState] = useImmer<HistoricStatsState>(setInitialState());

  const data = useQueryHistoricStats(projectID, {
    aggPeriod: state.aggPeriod,
    toDate: state.toDate,
    fromDate: state.fromDate,
    eventTypes: [state.eventType],
    includeChildren: true
  });

  const handlers = {
    onChangeAggPeriod(aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod) {
      setState((draft) => {
        draft.aggPeriod = aggPeriod;
        draft.fromDate = calculateFromDate(state.toDate, aggPeriod);
      });
    },
    onChangeEventType(eventType: HistoricOrderStatsEventTypeOptions) {
      setState((draft) => {
        draft.eventType = eventType;
      });
    }
  };

  return { data, handlers, state } as const;
}

function setInitialState(): HistoricStatsState {
  const initialToDate = dayjs().endOf('day');
  const initialToDateString = initialToDate.toISOString();

  return {
    aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod.Day,
    eventType: KnownHistoricDeliveryStatsEventTypes.Deliveries,
    toDate: initialToDateString,
    fromDate: calculateFromDate(initialToDateString, KnownHistoricDeliveryStatsAggregationPeriod.Day)
  };
}

function calculateFromDate(toDateString: string, aggPeriod: KnownHistoricDeliveryStatsAggregationPeriod) {
  const toDate = dayjs(toDateString);
  const fromDate = fromDateMaps[aggPeriod](toDate);
  return fromDate.toISOString();
}

const fromDateMaps: Record<KnownHistoricDeliveryStatsAggregationPeriod, (date: dayjs.Dayjs) => dayjs.Dayjs> = {
  day: (date) => date.subtract(1, 'month'),
  week: (date) => date.subtract(6, 'months').startOf('week'),
  weekday: (date) => date.subtract(6, 'months').startOf('week'),
  month: (date) => date.subtract(11, 'months').startOf('month'),
  year: (date) => date.subtract(1, 'year').startOf('year')
};
